import { Injectable } from '@angular/core';
import { AppConstants } from '../constants/app.contants';

/**
 * Mock Data Service
 * Comprehensive mock data service for all APIs in the project
 */
@Injectable({
  providedIn: 'root'
})
export class MockDataService {

  private readonly demoToken = 'eyJhbGciOiJub25lIiwidHlwIjoiSldUIn0.eyJVc2VySWQiOiJWUC1ERU1PLTAwMSIsIkZ1bGxOYW1lIjoiR3VsZiBEaWdpdGFsIFNvbHV0aW9ucyBXLkwuTC4iLCJFbWFpbEFkZHJlc3MiOiJ2ZW5kb3JAZGVtby5xYSIsIlBob25lIjoiKzk3NCA1NTUwIDAyMDAiLCJDb250YWN0VHlwZSI6IlByaW1hcnkiLCJleHAiOjQxMDI0NDQ4MDB9.demo';

  constructor() { }

  /**
   * Get mock data based on URL and method
   */
  getMockData(url: string, method: string, body?: any): any {
    const urlLower = url.toLowerCase();
    const methodUpper = method.toUpperCase();
    
    // Extract the relevant part of URL (after /api/)
    const apiIndex = urlLower.indexOf('/api/');
    const relevantPath = apiIndex >= 0 ? urlLower.substring(apiIndex + 5) : urlLower;
    
    console.log('🔍 MockDataService - URL:', url);
    console.log('🔍 Relevant Path:', relevantPath);
    console.log('🔍 Method:', methodUpper);
    
    // Authentication & Account
    if (relevantPath.includes('account/authenticate') || relevantPath.includes('authenticate')) {
      return this.getAuthenticateResponse();
    }
    
    if (relevantPath.includes('verify-otp') || relevantPath.includes('verifyotp')) {
      return this.getVerifyOTPResponse();
    }
    
    if (relevantPath.includes('generateotp')) {
      return this.getGenerateOTPResponse();
    }

    if (relevantPath.includes('validatesession')) {
      return { isSuccess: true, data: true };
    }

    if (relevantPath.includes('validatecontactemail') || relevantPath.includes('validatecontactmobile')) {
      return { isSuccess: true, data: { isAvailable: true, message: 'Available in demo mode' } };
    }

    if (relevantPath.includes('checkemailinuse')) {
      return { isSuccess: true, data: false };
    }

    // Accept/reject participation in demo mode. Acceptance must return a bid
    // identifier because the success dialog redirects directly to that bid.
    if (methodUpper === 'POST' && relevantPath.includes('tenders/participateintender')) {
      const tenderId = body?.TenderID || body?.tenderID || 'TND002';
      const isAccepted = String(body?.Action || body?.action || '').toLowerCase().includes('acc');
      return {
        isSuccess: true,
        data: {
          tenderID: tenderId,
          bidID: isAccepted ? `BID-${tenderId}-DEMO` : null,
          action: body?.Action || body?.action,
          status: isAccepted ? 'Draft' : 'Rejected'
        },
        message: isAccepted ? 'Tender participation accepted' : 'Tender participation rejected'
      };
    }

    // Tender Details - GET specific tender by ID (e.g., /api/tenders/TND001)
    if (methodUpper === 'GET' && relevantPath.includes('tenders/') && 
        !relevantPath.includes('gettenders') && 
        !relevantPath.includes('get-tenders') &&
        !relevantPath.includes('submittender') &&
        !relevantPath.includes('submit') &&
        !relevantPath.includes('getbiddetails') &&
        !relevantPath.includes('bids') &&
        !relevantPath.includes('bid')) {
      // Extract tender ID from URL (e.g., "tenders/TND001" -> "TND001")
      const tenderIdMatch = relevantPath.match(/tenders\/([^\/\?]+)/);
      if (tenderIdMatch && tenderIdMatch[1]) {
        const tenderId = tenderIdMatch[1];
        console.log('✅ Matched: Tender Details GET for ID:', tenderId);
        return this.getTenderDetailsResponse(tenderId);
      }
    }

    // Tenders - Improved matching (case-insensitive)
    if ((relevantPath.includes('tenders/gettenders') || 
         relevantPath.includes('tenders/get-tenders') ||
         relevantPath === 'tenders/gettenders' ||
         (relevantPath.includes('tenders') && methodUpper === 'GET' && !relevantPath.includes('submittender') && !relevantPath.includes('submit'))) &&
        !relevantPath.includes('getbiddetails') &&
        !relevantPath.includes('bids') &&
        !relevantPath.includes('bid')) {
      console.log('✅ Matched: Tenders GET');
      return this.getTendersResponse();
    }

    // Submit Tender (POST)
    if (methodUpper === 'POST' && (relevantPath.includes('tenders/submittender') || relevantPath.includes('submittender'))) {
      return this.getSubmitTenderResponse(body);
    }

    // Bids - Improved matching (case-insensitive)
    if ((relevantPath.includes('bids/getbids') || 
         relevantPath.includes('bids/get-bids') ||
         relevantPath === 'bids/getbids' ||
         (relevantPath.includes('bids') && methodUpper === 'GET' && !relevantPath.includes('getbiddetails') && !relevantPath.includes('submitbid') && !relevantPath.includes('submit'))) &&
        !relevantPath.includes('tenders') &&
        !relevantPath.includes('tender')) {
      console.log('✅ Matched: Bids GET');
      return this.getBidsResponse();
    }
    
    if (relevantPath.includes('getbiddetails') || relevantPath.includes('bids/getbiddetails')) {
      return this.getBidDetailsResponse();
    }

    // Submit Bid (POST)
    if (methodUpper === 'POST' && (relevantPath.includes('bids/submitbid') || relevantPath.includes('submitbid'))) {
      return this.getSubmitBidResponse(body);
    }

    // Purchase Orders. This must only match the PO endpoint; invoice, payment
    // and GRN endpoints use the same controller and have dedicated shapes.
    if (methodUpper === 'GET' && relevantPath.includes('purchaseorder/getpurchaseorders')) {
      return this.getPurchaseOrdersResponse();
    }

    // Confirm PO (POST/GET)
    if (relevantPath.includes('confirmpo') || relevantPath.includes('confirm')) {
      return this.getConfirmPOResponse();
    }

    // Update/Create Invoice (POST/PUT)
    if ((methodUpper === 'POST' || methodUpper === 'PUT') && (relevantPath.includes('updateorcreateinvoice') || relevantPath.includes('invoice'))) {
      return this.getCreateInvoiceResponse(body);
    }

    // Request GRN (POST)
    if (methodUpper === 'POST' && (relevantPath.includes('requestgrn') || relevantPath.includes('request-grn'))) {
      return this.getRequestGRNResponse(body);
    }

    // Invoices
    if (relevantPath.includes('invoices') || relevantPath.includes('getinvoicespurchaseorders')) {
      return this.getInvoicesResponse();
    }

    // Payments
    if (relevantPath.includes('payments') || relevantPath.includes('getpayments')) {
      return this.getPaymentsResponse();
    }

    // GRNs
    if (relevantPath.includes('grns') || relevantPath.includes('getgrnspurchaseorders')) {
      return this.getGRNsResponse();
    }

    // Send Message (POST)
    if (methodUpper === 'POST' && (relevantPath.includes('messages/sendmessage') || relevantPath.includes('sendmessage'))) {
      return this.getSendMessageResponse(body);
    }

    // Messages (POST for GetMessages)
    if (methodUpper === 'POST' && (relevantPath.includes('messages/getmessages') || relevantPath.includes('getmessages'))) {
      return this.getMessagesResponse();
    }

    // Messages (GET - fallback)
    if (relevantPath.includes('messages/getmessages') || (relevantPath.includes('messages') && methodUpper === 'GET')) {
      return this.getMessagesResponse();
    }

    // Notifications
    if (relevantPath.includes('notificaitons/getnotifications') || relevantPath.includes('notifications')) {
      return this.getNotificationsResponse();
    }

    // Register Vendor (POST)
    if (methodUpper === 'POST' && (relevantPath.includes('registervendor') || relevantPath.includes('register'))) {
      return this.getRegisterVendorResponse(body);
    }

    // Update Vendor (PUT/POST)
    if ((methodUpper === 'PUT' || methodUpper === 'POST') && (relevantPath.includes('updatevendor') || relevantPath.includes('vendor/update'))) {
      return this.getUpdateVendorResponse(body);
    }

    // Vendor Profile / GetVendorDetails
    if (relevantPath.includes('vendor/getprofile') || 
        relevantPath.includes('vendor/getvendordetails') ||
        (relevantPath.includes('vendor') && methodUpper === 'GET' && (relevantPath.includes('getvendordetails') || relevantPath.includes('getprofile')))) {
      console.log('✅ Matched: Vendor GetProfile/GetVendorDetails');
      return this.getVendorProfileResponse();
    }

    // Submit Bank Account (POST)
    if (methodUpper === 'POST' && (relevantPath.includes('bankapi/submitbankaccount') || relevantPath.includes('submitbankaccount'))) {
      return this.getSubmitBankAccountResponse(body);
    }

    // Update Bank Account (PUT/POST)
    if ((methodUpper === 'PUT' || methodUpper === 'POST') && (relevantPath.includes('updatebankdetails') || relevantPath.includes('bank/update'))) {
      return this.getUpdateBankAccountResponse(body);
    }

    // Bank Accounts
    if (relevantPath.includes('bankapi/getbankaccounts') || relevantPath.includes('bankaccounts')) {
      return this.getBankAccountsResponse();
    }

    // Lookups
    if (relevantPath.includes('lookups/all-lookups') || relevantPath.includes('lookups')) {
      return this.getLookupsResponse();
    }

    // Dashboard - Common/GetDashboardData
    if (relevantPath.includes('common/getdashboarddata') || 
        relevantPath.includes('getdashboarddata') || 
        (relevantPath.includes('common') && relevantPath.includes('dashboard'))) {
      return this.getDashboardDataResponse();
    }

    // Documents
    if (relevantPath.includes('documents/getdocuments')) {
      return this.getDocumentsResponse();
    }

    // Upload Documents (POST)
    if (methodUpper === 'POST' && (relevantPath.includes('uploaddocuments') || relevantPath.includes('upload'))) {
      return this.getUploadDocumentsResponse(body);
    }

    // Default response - Comprehensive fallback
    console.warn('⚠️ No specific mock data found for:', url);
    console.warn('⚠️ Relevant Path:', relevantPath);
    console.warn('⚠️ Method:', methodUpper);
    
    // Attempt to return default data based on path
    if (relevantPath.includes('tenders') && methodUpper === 'GET' && !relevantPath.includes('bid')) {
      console.log('⚠️ Fallback: Returning Tenders data');
      return this.getTendersResponse();
    }
    
    if (relevantPath.includes('bids') && methodUpper === 'GET' && !relevantPath.includes('tender')) {
      console.log('⚠️ Fallback: Returning Bids data');
      return this.getBidsResponse();
    }
    
    return this.getDefaultResponse(methodUpper);
  }

  // ==================== Authentication ====================
  private getAuthenticateResponse(): any {
    return {
      isSuccess: true,
      data: {
        token: this.demoToken,
        refreshToken: 'vp-demo-refresh-token',
        expiresIn: 3600,
        user: {
          Id: 'VENDOR001',
          FullName: 'Mock Vendor Company',
          EmailAddress: 'vendor@example.com',
          MobilePhone: '+974 1234 5678',
          Status: 'Active'
        }
      },
      message: 'Authentication successful'
    };
  }

  private getVerifyOTPResponse(): any {
    return {
      isSuccess: true,
      data: {
        verified: true,
        token: this.demoToken,
        refreshToken: 'vp-demo-refresh-token'
      },
      message: 'OTP verified successfully'
    };
  }

  private getGenerateOTPResponse(): any {
    return {
      isSuccess: true,
      data: {
        otpSent: true,
        expiryTime: new Date(Date.now() + 5 * 60 * 1000).toISOString()
      },
      message: 'OTP sent successfully'
    };
  }

  // ==================== Tenders ====================
  private getRelativeDemoDate(days: number): string {
    const date = new Date();
    date.setHours(12, 0, 0, 0);
    date.setDate(date.getDate() + days);
    return date.toISOString();
  }

  private getTendersResponse(): any {
    return {
      isSuccess: true,
      isSystemError: false,
      code: 200,
      data: [
        {
          tenderID: 'TND001',
          referenceNo: 'REF-2026-001',
          startDate: this.getRelativeDemoDate(-7),
          closureDate: this.getRelativeDemoDate(21),
          extendedDate: this.getRelativeDemoDate(28),
          bidClosureDate: this.getRelativeDemoDate(35),
          status: 'Open',
          publishDate: this.getRelativeDemoDate(-10),
          typeofTender: 'Public',
          bankName: 'Qatar National Bank',
          description: 'Construction of Office Building in Doha',
          entity: 'Qatar Foundation',
          Manageby: 'Qatar Foundation',
          TenderType: 'Public',
          action: 'Accepted',
          isNew: false,
          joiningFee: 5000,
          tenderClassification: 'A',
          isTenderBondRequired: true,
          isPerformanceBondRequired: true,
          isJoiningFeeRequired: true,
          vendorTenderParticipation: {
            tenderID: 'TND001',
            vendorID: 'VENDOR001',
            firstTimeView: false,
            joiningFee: 5000,
            isFeepaid: true
          }
        },
        {
          tenderID: 'TND002',
          referenceNo: 'REF-2026-002',
          startDate: this.getRelativeDemoDate(-3),
          closureDate: this.getRelativeDemoDate(30),
          extendedDate: this.getRelativeDemoDate(37),
          bidClosureDate: this.getRelativeDemoDate(44),
          status: 'Open',
          publishDate: this.getRelativeDemoDate(-5),
          typeofTender: 'Public',
          bankName: 'Commercial Bank',
          description: 'IT Infrastructure Upgrade Project',
          entity: 'Ministry of Finance',
          Manageby: 'Ministry of Finance',
          TenderType: 'Public',
          action: null,
          isNew: true,
          joiningFee: 3000,
          tenderClassification: 'B',
          isTenderBondRequired: false,
          isPerformanceBondRequired: true,
          isJoiningFeeRequired: true,
          vendorTenderParticipation: {
            tenderID: 'TND002',
            vendorID: 'VENDOR001',
            firstTimeView: true,
            joiningFee: 3000,
            isFeepaid: false,
            action: null
          }
        },
        {
          tenderID: 'TND003',
          referenceNo: 'REF-2026-003',
          startDate: this.getRelativeDemoDate(-14),
          closureDate: this.getRelativeDemoDate(14),
          extendedDate: this.getRelativeDemoDate(20),
          bidClosureDate: this.getRelativeDemoDate(27),
          status: 'Open',
          publishDate: this.getRelativeDemoDate(-16),
          typeofTender: 'Private',
          bankName: 'Doha Bank',
          description: 'Annual Maintenance Services',
          entity: 'Qatar Airways',
          Manageby: 'Qatar Airways',
          TenderType: 'Private',
          action: 'Accepted',
          isNew: false,
          joiningFee: 2000,
          tenderClassification: 'C',
          isTenderBondRequired: false,
          isPerformanceBondRequired: false,
          isJoiningFeeRequired: true,
          vendorTenderParticipation: {
            tenderID: 'TND003',
            vendorID: 'VENDOR001',
            firstTimeView: false,
            joiningFee: 2000,
            isFeepaid: true
          }
        },
        {
          tenderID: 'TND004',
          referenceNo: 'REF-2026-004',
          startDate: this.getRelativeDemoDate(-1),
          closureDate: this.getRelativeDemoDate(45),
          extendedDate: this.getRelativeDemoDate(52),
          bidClosureDate: this.getRelativeDemoDate(60),
          status: 'Open',
          publishDate: this.getRelativeDemoDate(-2),
          typeofTender: 'Public',
          bankName: 'Qatar Islamic Bank',
          description: 'Supply of Office Furniture and Equipment',
          entity: 'Ministry of Education',
          Manageby: 'Ministry of Education',
          TenderType: 'Public',
          action: 'Accepted',
          isNew: false,
          joiningFee: 4000,
          tenderClassification: 'B',
          isTenderBondRequired: true,
          isPerformanceBondRequired: true,
          isJoiningFeeRequired: true,
          vendorTenderParticipation: {
            tenderID: 'TND004',
            vendorID: 'VENDOR001',
            firstTimeView: false,
            joiningFee: 4000,
            isFeepaid: true
          }
        },
        {
          tenderID: 'TND005',
          referenceNo: 'REF-2026-005',
          startDate: this.getRelativeDemoDate(-5),
          closureDate: this.getRelativeDemoDate(18),
          extendedDate: this.getRelativeDemoDate(25),
          bidClosureDate: this.getRelativeDemoDate(32),
          status: 'Open',
          publishDate: this.getRelativeDemoDate(-7),
          typeofTender: 'Private',
          bankName: 'Al Rayan Bank',
          description: 'Security Systems Installation',
          entity: 'Hamad International Airport',
          Manageby: 'Hamad International Airport',
          TenderType: 'Private',
          action: null,
          isNew: true,
          joiningFee: 0,
          tenderClassification: 'A',
          isTenderBondRequired: true,
          isPerformanceBondRequired: true,
          isJoiningFeeRequired: false,
          vendorTenderParticipation: {
            tenderID: 'TND005',
            vendorID: 'VENDOR001',
            firstTimeView: true,
            joiningFee: 0,
            isFeepaid: true,
            action: null
          }
        }
      ],
      totalCount: 5
    };
  }

  // ==================== Tender Details ====================
  private getTenderDetailsResponse(tenderId: string): any {
    // Get tender from list or create a default one
    const tenders = this.getTendersResponse().data;
    let tender = tenders.find((t: any) =>
      String(t.tenderID).toLowerCase() === String(tenderId).toLowerCase()
    );
    
    // If tender not found, create a default one
    if (!tender) {
      tender = {
        tenderID: tenderId,
        referenceNo: 'REF-DEMO-001',
        startDate: this.getRelativeDemoDate(-7),
        closureDate: this.getRelativeDemoDate(21),
        extendedDate: this.getRelativeDemoDate(28),
        bidClosureDate: this.getRelativeDemoDate(35),
        status: 'Open',
        publishDate: this.getRelativeDemoDate(-10),
        typeofTender: 'Public',
        bankName: 'Qatar National Bank',
        description: 'Construction of Office Building in Doha',
        entity: 'Qatar Foundation',
        managedBy: 'Qatar Foundation',
        joiningFee: 5000,
        tenderClassification: 'A',
        isTenderBondRequired: true,
        isPerformanceBondRequired: true,
        isJoiningFeeRequired: true,
        action: null,
        bidID: null
      };
    }

    // Ensure managedBy is set (some mock data uses Manageby)
    if (!tender.managedBy && tender.Manageby) {
      tender.managedBy = tender.Manageby;
    }

    // Create a copy to avoid mutating the original tender from the list
    tender = { ...tender };

    // Add documents (attachments) grouped by attachmentFor
    tender.documents = [
      {
        name: 'Tender Document - Technical Specifications.pdf',
        description: 'Complete technical specifications and requirements',
        date: new Date('2024-01-10T00:00:00'),
        url: '#',
        attachmentFor: 'Tender Documents',
        fileContent: 'base64encodedcontent',
        additionalDetails: 'Technical specifications document',
        mimeType: 'application/pdf'
      },
      {
        name: 'Tender Document - Terms and Conditions.pdf',
        description: 'Terms and conditions for participation',
        date: new Date('2024-01-10T00:00:00'),
        url: '#',
        attachmentFor: 'Tender Documents',
        fileContent: 'base64encodedcontent',
        additionalDetails: 'Terms and conditions document',
        mimeType: 'application/pdf'
      },
      {
        name: 'Technical Drawings - Floor Plans.pdf',
        description: 'Detailed architectural drawings and floor plans',
        date: new Date('2024-01-12T00:00:00'),
        url: '#',
        attachmentFor: 'Technical Specifications',
        fileContent: 'base64encodedcontent',
        additionalDetails: 'Architectural drawings',
        mimeType: 'application/pdf'
      },
      {
        name: 'Technical Drawings - Electrical Layouts.pdf',
        description: 'Electrical system layouts and specifications',
        date: new Date('2024-01-12T00:00:00'),
        url: '#',
        attachmentFor: 'Technical Specifications',
        fileContent: 'base64encodedcontent',
        additionalDetails: 'Electrical layouts',
        mimeType: 'application/pdf'
      },
      {
        name: 'Bid Submission Form.docx',
        description: 'Template for bid submission',
        date: new Date('2024-01-10T00:00:00'),
        url: '#',
        attachmentFor: 'Bid Forms',
        fileContent: 'base64encodedcontent',
        additionalDetails: 'Bid submission form template',
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      },
      {
        name: 'Price Schedule Template.xlsx',
        description: 'Excel template for price schedule',
        date: new Date('2024-01-10T00:00:00'),
        url: '#',
        attachmentFor: 'Bid Forms',
        fileContent: 'base64encodedcontent',
        additionalDetails: 'Price schedule template',
        mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    ];

    // Add evaluation form criteria
    tender.evaluationForm = [
      {
        tenderID: tenderId,
        vendorID: 'VENDOR001',
        bidID: null,
        serialNo: 1,
        parameter: 'Technical Competence and Experience',
        maximumMark: 30,
        marksScored: null
      },
      {
        tenderID: tenderId,
        vendorID: 'VENDOR001',
        bidID: null,
        serialNo: 2,
        parameter: 'Project Management and Methodology',
        maximumMark: 25,
        marksScored: null
      },
      {
        tenderID: tenderId,
        vendorID: 'VENDOR001',
        bidID: null,
        serialNo: 3,
        parameter: 'Financial Proposal and Pricing',
        maximumMark: 30,
        marksScored: null
      },
      {
        tenderID: tenderId,
        vendorID: 'VENDOR001',
        bidID: null,
        serialNo: 4,
        parameter: 'Quality Assurance and Compliance',
        maximumMark: 10,
        marksScored: null
      },
      {
        tenderID: tenderId,
        vendorID: 'VENDOR001',
        bidID: null,
        serialNo: 5,
        parameter: 'Timeline and Delivery Schedule',
        maximumMark: 5,
        marksScored: null
      }
    ];

    // Ensure vendorTenderParticipation structure is complete
    if (!tender.vendorTenderParticipation) {
      tender.vendorTenderParticipation = {
        tenderID: tenderId,
        vendorID: 'VENDOR001',
        firstTimeView: false,
        joiningFee: tender.joiningFee || 0,
        isFeepaid: true,
        action: null
      };
    } else {
      // Ensure the action is set correctly
      tender.vendorTenderParticipation.action = tender.action || tender.vendorTenderParticipation.action || null;
    }

    return {
      isSuccess: true,
      isSystemError: false,
      code: 200,
      data: tender,
      message: 'Tender details retrieved successfully'
    };
  }

  // ==================== Bids ====================
  private getBidsResponse(): any {
    return {
      isSuccess: true,
      isSystemError: false,
      code: 200,
      data: [
        {
          bidID: 'BID001',
          tenderID: 'TND001',
          tenderReferenceNo: 'REF-2024-001',
          tenderDescription: 'Construction of Office Building in Doha',
          status: 'Submitted',
          bidValue: 1500000,
          bidStartDate: '2024-01-15T00:00:00',
          bidClosingDate: '2024-02-15T00:00:00',
          bidValidUntil: '2024-08-15T00:00:00',
          canSubmit: false,
          vendorID: 'VENDOR001',
          submittedDate: '2024-02-10T10:30:00',
          extendedDate: '2024-02-20T00:00:00',
          isNew: false,
          BidID: 'BID001',
          Status: 'Submitted',
          BidStartDate: '2024-01-15T00:00:00',
          BidClosingDate: '2024-02-15T00:00:00',
          ExtendedDate: '2024-02-20T00:00:00',
          tender: {
            tenderID: 'TND001',
            startDate: '2024-01-15T00:00:00',
            closureDate: '2024-02-15T00:00:00',
            extendedDate: '2024-02-20T00:00:00'
          }
        },
        {
          bidID: 'BID002',
          tenderID: 'TND002',
          tenderReferenceNo: 'REF-2024-002',
          tenderDescription: 'IT Infrastructure Upgrade Project',
          status: 'Draft',
          bidValue: 1250000,
          bidStartDate: '2024-02-01T00:00:00',
          bidClosingDate: '2024-03-01T00:00:00',
          bidValidUntil: this.getRelativeDemoDate(120),
          canSubmit: true,
          vendorID: 'VENDOR001',
          submittedDate: null,
          extendedDate: this.getRelativeDemoDate(37),
          isNew: true,
          BidID: 'BID002',
          Status: 'Draft',
          BidStartDate: '2024-02-01T00:00:00',
          BidClosingDate: '2024-03-01T00:00:00',
          ExtendedDate: this.getRelativeDemoDate(37),
          tender: {
            tenderID: 'TND002',
            startDate: '2024-02-01T00:00:00',
            closureDate: this.getRelativeDemoDate(30),
            extendedDate: this.getRelativeDemoDate(37)
          }
        },
        {
          bidID: 'BID003',
          tenderID: 'TND003',
          tenderReferenceNo: 'REF-2024-003',
          tenderDescription: 'Annual Maintenance Services',
          status: 'Awarded',
          bidValue: 850000,
          bidStartDate: '2024-01-20T00:00:00',
          bidClosingDate: '2024-02-20T00:00:00',
          bidValidUntil: '2024-08-20T00:00:00',
          canSubmit: false,
          vendorID: 'VENDOR001',
          submittedDate: '2024-02-15T14:20:00',
          extendedDate: '2024-02-25T00:00:00',
          isNew: false,
          BidID: 'BID003',
          Status: 'Awarded',
          BidStartDate: '2024-01-20T00:00:00',
          BidClosingDate: '2024-02-20T00:00:00',
          ExtendedDate: '2024-02-25T00:00:00',
          tender: {
            tenderID: 'TND003',
            startDate: '2024-01-20T00:00:00',
            closureDate: '2024-02-20T00:00:00',
            extendedDate: '2024-02-25T00:00:00'
          }
        },
        {
          bidID: 'BID004',
          tenderID: 'TND004',
          tenderReferenceNo: 'REF-2024-004',
          tenderDescription: 'Supply of Office Furniture and Equipment',
          status: 'Submitted',
          bidValue: 1250000,
          bidStartDate: '2024-02-10T00:00:00',
          bidClosingDate: '2024-03-10T00:00:00',
          bidValidUntil: '2024-09-10T00:00:00',
          canSubmit: false,
          vendorID: 'VENDOR001',
          submittedDate: '2024-03-05T09:15:00',
          extendedDate: '2024-03-15T00:00:00',
          isNew: false,
          BidID: 'BID004',
          Status: 'Submitted',
          BidStartDate: '2024-02-10T00:00:00',
          BidClosingDate: '2024-03-10T00:00:00',
          ExtendedDate: '2024-03-15T00:00:00',
          tender: {
            tenderID: 'TND004',
            startDate: '2024-02-10T00:00:00',
            closureDate: '2024-03-10T00:00:00',
            extendedDate: '2024-03-15T00:00:00'
          }
        },
        {
          bidID: 'BID005',
          tenderID: 'TND005',
          tenderReferenceNo: 'REF-2024-005',
          tenderDescription: 'Security Systems Installation',
          status: 'Draft',
          bidValue: 950000,
          bidStartDate: '2024-02-15T00:00:00',
          bidClosingDate: '2024-03-15T00:00:00',
          bidValidUntil: this.getRelativeDemoDate(120),
          canSubmit: true,
          vendorID: 'VENDOR001',
          submittedDate: null,
          extendedDate: this.getRelativeDemoDate(25),
          isNew: true,
          BidID: 'BID005',
          Status: 'Draft',
          BidStartDate: '2024-02-15T00:00:00',
          BidClosingDate: '2024-03-15T00:00:00',
          ExtendedDate: this.getRelativeDemoDate(25),
          tender: {
            tenderID: 'TND005',
            startDate: '2024-02-15T00:00:00',
            closureDate: this.getRelativeDemoDate(18),
            extendedDate: this.getRelativeDemoDate(25)
          }
        }
      ],
      totalCount: 5
    };
  }

  private getBidDetailsResponse(): any {
    return {
      isSuccess: true,
      data: {
        bidID: 'BID001',
        tenderID: 'TND001',
        status: 'Submitted',
        bidValue: 1500000,
        bidStartDate: '2024-01-15T00:00:00',
        bidClosingDate: '2024-02-15T00:00:00',
        bidValidUntil: '2024-08-15T00:00:00',
        vendorID: 'VENDOR001',
        tender: {
          tenderID: 'TND001',
          referenceNo: 'REF-2024-001',
          description: 'Construction of Office Building in Doha',
          entity: 'Qatar Foundation'
        },
        attachments: []
      }
    };
  }

  // ==================== Purchase Orders ====================
  // Record 1: PO001 -> GRN001 -> INV001 -> PAY001 (Completed Cycle)
  // Record 2: PO002 -> GRN002 -> INV002 -> PAY002 (In Progress)
  // Record 3: PO003 -> GRN003 -> INV003 -> PAY003 (Completed Cycle)
  // Record 4: PO004 -> GRN004 -> INV004 -> PAY004 (Partial Payment)
  // Record 5: PO005 -> GRN005 -> INV005 -> PAY005 (Pending)
  private getPurchaseOrdersResponse(): any {
    return {
      isSuccess: true,
      result: [
        {
          purchId: 'PO001',
          purchName: 'Purchase Order 001',
          purchStatus: 'Confirmed',
          vendorAccount: 'VENDOR001',
          vendorName: 'Mock Vendor Company',
          orderDate: '2024-01-15T00:00:00',
          transDateParsed: new Date('2024-01-15T00:00:00'),
          deliveryDate: '2024-02-15T00:00:00',
          status: 'Confirmed',
          currencyCode: 'QAR',
          totalAmount: 250000,
          poOrderer: 'John Smith',
          deliveryAddress: 'Al Corniche Street, Building 123, Doha',
          isConfirmed: true,
          lines: [
            {
              lineNumber: 1,
              itemNumber: 'ITEM001',
              itemDescription: 'Office Furniture Set',
              quantity: 50,
              unitPrice: 2000,
              lineAmount: 100000,
              postedQuantityValue: 30, // 30 received from GRN001
              quantityReceivable: 20, // 20 remaining
              unit: 'PCS'
            },
            {
              lineNumber: 2,
              itemNumber: 'ITEM002',
              itemDescription: 'Computer Equipment',
              quantity: 100,
              unitPrice: 1500,
              lineAmount: 150000,
              postedQuantityValue: 80, // 80 received from GRN002
              quantityReceivable: 20, // 20 remaining
              unit: 'PCS'
            }
          ]
        },
        {
          purchId: 'PO002',
          purchName: 'Purchase Order 002',
          purchStatus: 'Pending',
          vendorAccount: 'VENDOR001',
          vendorName: 'Mock Vendor Company',
          orderDate: '2024-02-01T00:00:00',
          transDateParsed: new Date('2024-02-01T00:00:00'),
          deliveryDate: '2024-03-01T00:00:00',
          status: 'Pending',
          currencyCode: 'QAR',
          totalAmount: 180000,
          poOrderer: 'Sarah Johnson',
          deliveryAddress: 'West Bay Area, Tower 456, Doha',
          isConfirmed: false,
          lines: [
            {
              lineNumber: 1,
              itemNumber: 'ITEM003',
              itemDescription: 'IT Equipment - Servers & Networking',
              quantity: 75,
              unitPrice: 2400,
              lineAmount: 180000,
              postedQuantityValue: 0, // Not received yet
              quantityReceivable: 75,
              unit: 'PCS'
            }
          ]
        },
        {
          purchId: 'PO003',
          purchName: 'Purchase Order 003',
          purchStatus: 'Completed',
          vendorAccount: 'VENDOR001',
          vendorName: 'Mock Vendor Company',
          orderDate: '2024-01-10T00:00:00',
          transDateParsed: new Date('2024-01-10T00:00:00'),
          deliveryDate: '2024-02-10T00:00:00',
          status: 'Completed',
          currencyCode: 'QAR',
          totalAmount: 320000,
          poOrderer: 'Ahmed Al-Mansouri',
          deliveryAddress: 'Industrial Area, Warehouse 789, Doha',
          isConfirmed: true,
          lines: [
            {
              lineNumber: 1,
              itemNumber: 'ITEM004',
              itemDescription: 'Construction Materials - Cement & Steel',
              quantity: 200,
              unitPrice: 1600,
              lineAmount: 320000,
              postedQuantityValue: 200, // All received from GRN003
              quantityReceivable: 0, // Completed
              unit: 'PCS'
            }
          ]
        },
        {
          purchId: 'PO004',
          purchName: 'Purchase Order 004',
          purchStatus: 'Confirmed',
          vendorAccount: 'VENDOR001',
          vendorName: 'Mock Vendor Company',
          orderDate: '2024-02-10T00:00:00',
          transDateParsed: new Date('2024-02-10T00:00:00'),
          deliveryDate: '2024-03-10T00:00:00',
          status: 'Confirmed',
          currencyCode: 'QAR',
          totalAmount: 450000,
          poOrderer: 'Fatima Al-Zahra',
          deliveryAddress: 'Al Sadd Street, Building 321, Doha',
          isConfirmed: true,
          lines: [
            {
              lineNumber: 1,
              itemNumber: 'ITEM005',
              itemDescription: 'Security Systems - CCTV & Access Control',
              quantity: 120,
              unitPrice: 3750,
              lineAmount: 450000,
              postedQuantityValue: 60, // 60 received from GRN004
              quantityReceivable: 60, // 60 remaining
              unit: 'PCS'
            }
          ]
        },
        {
          purchId: 'PO005',
          purchName: 'Purchase Order 005',
          purchStatus: 'Pending',
          vendorAccount: 'VENDOR001',
          vendorName: 'Mock Vendor Company',
          orderDate: '2024-02-20T00:00:00',
          transDateParsed: new Date('2024-02-20T00:00:00'),
          deliveryDate: '2024-03-20T00:00:00',
          status: 'Pending',
          currencyCode: 'QAR',
          totalAmount: 275000,
          poOrderer: 'Mohammed Al-Thani',
          deliveryAddress: 'Pearl-Qatar, Residential Complex, Doha',
          isConfirmed: false,
          lines: [
            {
              lineNumber: 1,
              itemNumber: 'ITEM006',
              itemDescription: 'Office Supplies - Stationery & Paper',
              quantity: 550,
              unitPrice: 500,
              lineAmount: 275000,
              postedQuantityValue: 0, // Not received yet
              quantityReceivable: 550,
              unit: 'PCS'
            }
          ]
        }
      ],
      totalCount: 5
    };
  }

  // ==================== Invoices ====================
  // Each Invoice is linked to corresponding PO and GRN
  private getInvoicesResponse(): any {
    return {
      isSuccess: true,
      result: {
        invoiceHeaders: [
          {
            invoiceId: 'INV001',
            invoiceNumber: 'INV-2024-001',
            invoiceDate: '2024-02-01T00:00:00',
            invoiceDateParsed: new Date('2024-02-01T00:00:00'),
            invoiceReceivedDate: '2024-02-05T00:00:00',
            invoiceReceivedDateParsed: new Date('2024-02-05T00:00:00'),
            invoiceDescription: 'Invoice for Office Furniture Set (30 PCS) from PO001 - GRN001',
            productReceiptNo: 'GRN001',
            purchId: 'PO001',
            status: 'Posted',
            invoiceAmount: 100000,
            currencyCode: 'QAR',
            recId: 'REC001',
            vendorAccount: 'VENDOR001',
            vendorName: 'Mock Vendor Company',
            expanded: false,
            lines: [
              {
                lineNumber: 1,
                lineDescription: 'Office Furniture Set - 30 PCS received via GRN001',
                quantity: 30,
                unitPrice: 2000,
                purchUnit: 'PCS',
                lineAmount: 60000
              },
              {
                lineNumber: 2,
                lineDescription: 'Computer Equipment - 20 PCS partial delivery',
                quantity: 20,
                unitPrice: 2000,
                purchUnit: 'PCS',
                lineAmount: 40000
              }
            ],
            poHeaderDetails: {
              currencyCode: 'QAR',
              totalAmount: 250000,
              purchId: 'PO001',
              purchName: 'Purchase Order 001 - Office Furniture & Computers'
            }
          },
          {
            invoiceId: 'INV002',
            invoiceNumber: 'INV-2024-002',
            invoiceDate: '2024-02-10T00:00:00',
            invoiceDateParsed: new Date('2024-02-10T00:00:00'),
            invoiceReceivedDate: '2024-02-12T00:00:00',
            invoiceReceivedDateParsed: new Date('2024-02-12T00:00:00'),
            invoiceDescription: 'Invoice for IT Equipment (75 PCS) from PO002 - GRN002',
            productReceiptNo: 'GRN002',
            purchId: 'PO002',
            status: 'Draft',
            invoiceAmount: 180000,
            currencyCode: 'QAR',
            recId: 'REC002',
            vendorAccount: 'VENDOR001',
            vendorName: 'Mock Vendor Company',
            expanded: false,
            lines: [
              {
                lineNumber: 1,
                lineDescription: 'IT Equipment - Servers & Networking - 75 PCS from GRN002',
                quantity: 75,
                unitPrice: 2400,
                purchUnit: 'PCS',
                lineAmount: 180000
              }
            ],
            poHeaderDetails: {
              currencyCode: 'QAR',
              totalAmount: 180000,
              purchId: 'PO002',
              purchName: 'Purchase Order 002 - IT Equipment'
            }
          },
          {
            invoiceId: 'INV003',
            invoiceNumber: 'INV-2024-003',
            invoiceDate: '2024-01-20T00:00:00',
            invoiceDateParsed: new Date('2024-01-20T00:00:00'),
            invoiceReceivedDate: '2024-01-25T00:00:00',
            invoiceReceivedDateParsed: new Date('2024-01-25T00:00:00'),
            invoiceDescription: 'Invoice for Construction Materials (200 PCS) from PO003 - GRN003',
            productReceiptNo: 'GRN003',
            purchId: 'PO003',
            status: 'Posted',
            invoiceAmount: 320000,
            currencyCode: 'QAR',
            recId: 'REC003',
            vendorAccount: 'VENDOR001',
            vendorName: 'Mock Vendor Company',
            expanded: false,
            lines: [
              {
                lineNumber: 1,
                lineDescription: 'Construction Materials - Cement & Steel - 200 PCS from GRN003',
                quantity: 200,
                unitPrice: 1600,
                purchUnit: 'PCS',
                lineAmount: 320000
              }
            ],
            poHeaderDetails: {
              currencyCode: 'QAR',
              totalAmount: 320000,
              purchId: 'PO003',
              purchName: 'Purchase Order 003 - Construction Materials'
            }
          },
          {
            invoiceId: 'INV004',
            invoiceNumber: 'INV-2024-004',
            invoiceDate: '2024-02-15T00:00:00',
            invoiceDateParsed: new Date('2024-02-15T00:00:00'),
            invoiceReceivedDate: '2024-02-18T00:00:00',
            invoiceReceivedDateParsed: new Date('2024-02-18T00:00:00'),
            invoiceDescription: 'Invoice for Security Systems (60 PCS) from PO004 - GRN004',
            productReceiptNo: 'GRN004',
            purchId: 'PO004',
            status: 'Posted',
            invoiceAmount: 225000,
            currencyCode: 'QAR',
            recId: 'REC004',
            vendorAccount: 'VENDOR001',
            vendorName: 'Mock Vendor Company',
            expanded: false,
            lines: [
              {
                lineNumber: 1,
                lineDescription: 'Security Systems - CCTV & Access Control - 60 PCS from GRN004',
                quantity: 60,
                unitPrice: 3750,
                purchUnit: 'PCS',
                lineAmount: 225000
              }
            ],
            poHeaderDetails: {
              currencyCode: 'QAR',
              totalAmount: 450000,
              purchId: 'PO004',
              purchName: 'Purchase Order 004 - Security Systems'
            }
          },
          {
            invoiceId: 'INV005',
            invoiceNumber: 'INV-2024-005',
            invoiceDate: '2024-02-25T00:00:00',
            invoiceDateParsed: new Date('2024-02-25T00:00:00'),
            invoiceReceivedDate: '2024-02-28T00:00:00',
            invoiceReceivedDateParsed: new Date('2024-02-28T00:00:00'),
            invoiceDescription: 'Invoice for Office Supplies (275 PCS) from PO005 - GRN005',
            productReceiptNo: 'GRN005',
            purchId: 'PO005',
            status: 'Draft',
            invoiceAmount: 137500,
            currencyCode: 'QAR',
            recId: null,
            vendorAccount: 'VENDOR001',
            vendorName: 'Mock Vendor Company',
            expanded: false,
            lines: [
              {
                lineNumber: 1,
                lineDescription: 'Office Supplies - Stationery & Paper - 275 PCS from GRN005',
                quantity: 275,
                unitPrice: 500,
                purchUnit: 'PCS',
                lineAmount: 137500
              }
            ],
            poHeaderDetails: {
              currencyCode: 'QAR',
              totalAmount: 275000,
              purchId: 'PO005',
              purchName: 'Purchase Order 005 - Office Supplies'
            }
          }
        ],
        poHeaderDetails: {
          currencyCode: 'QAR',
          totalAmount: 250000,
          purchId: 'PO001',
          purchName: 'Purchase Order 001 - Office Furniture & Computers'
        }
      },
      totalCount: 5
    };
  }

  // ==================== Payments ====================
  // Each Payment is linked to corresponding Invoice and PO
  private getPaymentsResponse(): any {
    return {
      isSuccess: true,
      result: [
        {
          paymentId: 'PAY001',
          paymentNumber: 'PAY-2024-001',
          paymentDate: '2024-02-15T00:00:00',
          paymentDateParsed: new Date('2024-02-15T00:00:00'),
          invoiceId: 'INV001',
          invoiceNumber: 'INV-2024-001',
          amount: 100000,
          paidAmount: 100000,
          currencyCode: 'QAR',
          currency: 'QAR',
          status: 'Paid',
          paymentMethod: 'Bank Transfer',
          vendorAccount: 'VENDOR001',
          vendorName: 'Mock Vendor Company',
          bankBeneficieryName: 'Mock Vendor Company',
          bankIBAN: 'QA12345678901234567890',
          bankAccountNumber: '1234567890',
          bankName: 'Qatar National Bank',
          expanded: false,
          lines: [
            {
              purchId: 'PO001',
              voucher: 'VCH-2024-001',
              invoiceId: 'INV001',
              invoiceNumber: 'INV-2024-001',
              currencyCode: 'QAR',
              amountDebit: 100000,
              amountCredit: 0
            }
          ]
        },
        {
          paymentId: 'PAY002',
          paymentNumber: 'PAY-2024-002',
          paymentDate: '2024-02-20T00:00:00',
          paymentDateParsed: new Date('2024-02-20T00:00:00'),
          invoiceId: 'INV002',
          invoiceNumber: 'INV-2024-002',
          amount: 180000,
          paidAmount: 180000,
          currencyCode: 'QAR',
          currency: 'QAR',
          status: 'Pending',
          paymentMethod: 'Cheque',
          vendorAccount: 'VENDOR001',
          vendorName: 'Mock Vendor Company',
          bankBeneficieryName: 'Mock Vendor Company',
          bankIBAN: 'QA12345678901234567890',
          bankAccountNumber: '1234567890',
          bankName: 'Qatar National Bank',
          expanded: false,
          lines: [
            {
              purchId: 'PO002',
              voucher: 'VCH-2024-002',
              invoiceId: 'INV002',
              invoiceNumber: 'INV-2024-002',
              currencyCode: 'QAR',
              amountDebit: 180000,
              amountCredit: 0
            }
          ]
        },
        {
          paymentId: 'PAY003',
          paymentNumber: 'PAY-2024-003',
          paymentDate: '2024-02-25T00:00:00',
          paymentDateParsed: new Date('2024-02-25T00:00:00'),
          invoiceId: 'INV003',
          invoiceNumber: 'INV-2024-003',
          amount: 320000,
          paidAmount: 320000,
          currencyCode: 'QAR',
          currency: 'QAR',
          status: 'Paid',
          paymentMethod: 'Bank Transfer',
          vendorAccount: 'VENDOR001',
          vendorName: 'Mock Vendor Company',
          bankBeneficieryName: 'Mock Vendor Company',
          bankIBAN: 'QA12345678901234567890',
          bankAccountNumber: '1234567890',
          bankName: 'Qatar National Bank',
          expanded: false,
          lines: [
            {
              purchId: 'PO003',
              voucher: 'VCH-2024-003',
              invoiceId: 'INV003',
              invoiceNumber: 'INV-2024-003',
              currencyCode: 'QAR',
              amountDebit: 320000,
              amountCredit: 0
            }
          ]
        },
        {
          paymentId: 'PAY004',
          paymentNumber: 'PAY-2024-004',
          paymentDate: '2024-03-01T00:00:00',
          paymentDateParsed: new Date('2024-03-01T00:00:00'),
          invoiceId: 'INV004',
          invoiceNumber: 'INV-2024-004',
          amount: 225000,
          paidAmount: 225000,
          currencyCode: 'QAR',
          currency: 'QAR',
          status: 'Pending',
          paymentMethod: 'Cheque',
          vendorAccount: 'VENDOR001',
          vendorName: 'Mock Vendor Company',
          bankBeneficieryName: 'Mock Vendor Company',
          bankIBAN: 'QA12345678901234567890',
          bankAccountNumber: '1234567890',
          bankName: 'Qatar National Bank',
          expanded: false,
          lines: [
            {
              purchId: 'PO004',
              voucher: 'VCH-2024-004',
              invoiceId: 'INV004',
              invoiceNumber: 'INV-2024-004',
              currencyCode: 'QAR',
              amountDebit: 225000,
              amountCredit: 0
            }
          ]
        },
        {
          paymentId: 'PAY005',
          paymentNumber: 'PAY-2024-005',
          paymentDate: '2024-03-05T00:00:00',
          paymentDateParsed: new Date('2024-03-05T00:00:00'),
          invoiceId: 'INV005',
          invoiceNumber: 'INV-2024-005',
          amount: 137500,
          paidAmount: 137500,
          currencyCode: 'QAR',
          currency: 'QAR',
          status: 'Pending',
          paymentMethod: 'Bank Transfer',
          vendorAccount: 'VENDOR001',
          vendorName: 'Mock Vendor Company',
          bankBeneficieryName: 'Mock Vendor Company',
          bankIBAN: 'QA12345678901234567890',
          bankAccountNumber: '1234567890',
          bankName: 'Qatar National Bank',
          expanded: false,
          lines: [
            {
              purchId: 'PO005',
              voucher: 'VCH-2024-005',
              invoiceId: 'INV005',
              invoiceNumber: 'INV-2024-005',
              currencyCode: 'QAR',
              amountDebit: 137500,
              amountCredit: 0
            }
          ]
        }
      ],
      totalCount: 5
    };
  }

  // ==================== GRNs ====================
  // Each GRN is linked to corresponding PO and Invoice
  // isExists = true if Invoice already exists
  private getGRNsResponse(): any {
    return {
      isSuccess: true,
      result: [
        {
          grnId: 'GRN001',
          grnNumber: 'GRN-2024-001',
          grnDate: '2024-02-01T00:00:00',
          productReceiptNo: 'GRN001',
          productReceiptDateParsed: new Date('2024-02-01T00:00:00'),
          purchId: 'PO001',
          status: 'Posted',
          totalQuantity: 30,
          isExists: false, // Invoice INV001 exists but new invoice can be created
          expanded: false,
          lines: [
            {
              lineNumber: 1,
              itemNumber: 'ITEM001',
              itemDescription: 'Office Furniture Set',
              lineDescription: 'Office Furniture Set - 30 PCS received from PO001 Line 1',
              quantity: 30,
              receivedQuantity: 30,
              orderedQuantity: 50, // From PO001 Line 1
              unitPrice: 2000,
              unit: 'PCS',
              postedQuantityValue: 30,
              quantityReceivable: 0
            }
          ]
        },
        {
          grnId: 'GRN002',
          grnNumber: 'GRN-2024-002',
          grnDate: '2024-02-10T00:00:00',
          productReceiptNo: 'GRN002',
          productReceiptDateParsed: new Date('2024-02-10T00:00:00'),
          purchId: 'PO002',
          status: 'Draft',
          totalQuantity: 75,
          isExists: false, // Invoice INV002 exists but in Draft status
          expanded: false,
          lines: [
            {
              lineNumber: 1,
              itemNumber: 'ITEM003',
              itemDescription: 'IT Equipment - Servers & Networking',
              lineDescription: 'IT Equipment - Servers & Networking - 75 PCS received from PO002 Line 1',
              quantity: 75,
              receivedQuantity: 75,
              orderedQuantity: 75, // From PO002 Line 1
              unitPrice: 2400,
              unit: 'PCS',
              postedQuantityValue: 0,
              quantityReceivable: 0
            }
          ]
        },
        {
          grnId: 'GRN003',
          grnNumber: 'GRN-2024-003',
          grnDate: '2024-01-25T00:00:00',
          productReceiptNo: 'GRN003',
          productReceiptDateParsed: new Date('2024-01-25T00:00:00'),
          purchId: 'PO003',
          status: 'Posted',
          totalQuantity: 200,
          isExists: true, // Invoice INV003 exists and Posted
          expanded: false,
          lines: [
            {
              lineNumber: 1,
              itemNumber: 'ITEM004',
              itemDescription: 'Construction Materials - Cement & Steel',
              lineDescription: 'Construction Materials - Cement & Steel - 200 PCS received from PO003 Line 1',
              quantity: 200,
              receivedQuantity: 200,
              orderedQuantity: 200, // From PO003 Line 1
              unitPrice: 1600,
              unit: 'PCS',
              postedQuantityValue: 200,
              quantityReceivable: 0
            }
          ]
        },
        {
          grnId: 'GRN004',
          grnNumber: 'GRN-2024-004',
          grnDate: '2024-02-15T00:00:00',
          productReceiptNo: 'GRN004',
          productReceiptDateParsed: new Date('2024-02-15T00:00:00'),
          purchId: 'PO004',
          status: 'Posted',
          totalQuantity: 60,
          isExists: false, // Invoice INV004 exists but new invoice can be created for remaining quantity
          expanded: false,
          lines: [
            {
              lineNumber: 1,
              itemNumber: 'ITEM005',
              itemDescription: 'Security Systems - CCTV & Access Control',
              lineDescription: 'Security Systems - CCTV & Access Control - 60 PCS received from PO004 Line 1',
              quantity: 60,
              receivedQuantity: 60,
              orderedQuantity: 120, // From PO004 Line 1 (60 remaining)
              unitPrice: 3750,
              unit: 'PCS',
              postedQuantityValue: 60,
              quantityReceivable: 0
            }
          ]
        },
        {
          grnId: 'GRN005',
          grnNumber: 'GRN-2024-005',
          grnDate: '2024-02-25T00:00:00',
          productReceiptNo: 'GRN005',
          productReceiptDateParsed: new Date('2024-02-25T00:00:00'),
          purchId: 'PO005',
          status: 'Draft',
          totalQuantity: 275,
          isExists: false, // Invoice INV005 exists but in Draft status
          expanded: false,
          lines: [
            {
              lineNumber: 1,
              itemNumber: 'ITEM006',
              itemDescription: 'Office Supplies - Stationery & Paper',
              lineDescription: 'Office Supplies - Stationery & Paper - 275 PCS received from PO005 Line 1',
              quantity: 275,
              receivedQuantity: 275,
              orderedQuantity: 550, // From PO005 Line 1 (275 remaining)
              unitPrice: 500,
              unit: 'PCS',
              postedQuantityValue: 0,
              quantityReceivable: 275
            }
          ]
        }
      ],
      totalCount: 5
    };
  }

  // ==================== Messages ====================
  private getMessagesResponse(): any {
    return {
      isSuccess: true,
      data: [
        {
          messageId: 'MSG001',
          messageContent: 'Please provide additional documentation for invoice INV-2024-001',
          direction: 'incoming',
          moudle: 'financials',
          referenceNumber: 'INV-2024-001',
          subReferenceNumber: 'PO001',
          vendorID: 'VENDOR001',
          createdAt: '2024-02-05T10:30:00',
          CreatedAt: '2024-02-05T10:30:00'
        },
        {
          messageId: 'MSG002',
          messageContent: 'We have submitted the requested documents',
          direction: 'outgoing',
          moudle: 'financials',
          referenceNumber: 'INV-2024-001',
          subReferenceNumber: 'PO001',
          vendorID: 'VENDOR001',
          createdAt: '2024-02-05T14:20:00',
          CreatedAt: '2024-02-05T14:20:00'
        },
        {
          messageId: 'MSG003',
          messageContent: 'Your bid has been reviewed and approved',
          direction: 'incoming',
          moudle: 'bidding',
          referenceNumber: 'BID001',
          subReferenceNumber: 'TND001',
          vendorID: 'VENDOR001',
          createdAt: '2024-02-10T09:15:00',
          CreatedAt: '2024-02-10T09:15:00'
        },
        {
          messageId: 'MSG004',
          messageContent: 'General inquiry about tender process',
          direction: 'incoming',
          moudle: 'general',
          referenceNumber: 'TND002',
          subReferenceNumber: null,
          vendorID: 'VENDOR001',
          createdAt: '2024-02-12T11:45:00',
          CreatedAt: '2024-02-12T11:45:00'
        },
        {
          messageId: 'MSG005',
          messageContent: 'Payment confirmation for invoice INV-2024-003',
          direction: 'incoming',
          moudle: 'financials',
          referenceNumber: 'INV-2024-003',
          subReferenceNumber: 'PO003',
          vendorID: 'VENDOR001',
          createdAt: '2024-02-15T16:30:00',
          CreatedAt: '2024-02-15T16:30:00'
        }
      ],
      totalCount: 5
    };
  }

  // ==================== Notifications ====================
  private getNotificationsResponse(): any {
    return {
      isSuccess: true,
      result: [
        {
          notificationId: 'NOTIF001',
          title: 'New Tender Available',
          message: 'A new tender has been published: REF-2024-001',
          type: 'tender',
          isRead: false,
          createdAt: '2024-01-10T08:00:00',
          link: '/tender/tenderdetails/TND001'
        },
        {
          notificationId: 'NOTIF002',
          title: 'Payment Received',
          message: 'Payment PAY-2024-001 has been processed',
          type: 'payment',
          isRead: false,
          createdAt: '2024-02-15T10:30:00',
          link: '/financials'
        },
        {
          notificationId: 'NOTIF003',
          title: 'Bid Status Updated',
          message: 'Your bid BID001 status has been updated',
          type: 'bid',
          isRead: true,
          createdAt: '2024-02-10T09:15:00',
          link: '/bid/biddetails/BID001'
        },
        {
          notificationId: 'NOTIF004',
          title: 'Invoice Posted',
          message: 'Invoice INV-2024-001 has been posted',
          type: 'invoice',
          isRead: true,
          createdAt: '2024-02-01T14:20:00',
          link: '/financials'
        },
        {
          notificationId: 'NOTIF005',
          title: 'GRN Received',
          message: 'GRN GRN-2024-004 has been received for PO004',
          type: 'grn',
          isRead: false,
          createdAt: '2024-02-15T11:00:00',
          link: '/financials'
        }
      ],
      totalCount: 5
    };
  }

  // ==================== Vendor Profile ====================
  private getVendorProfileResponse(): any {
    return {
      isSuccess: true,
      isSystemError: false,
      code: 200,
      data: {
        vendorID: 'VENDOR001',
        erpVendorID: 'ERP001',
        vendorName: 'Mock Vendor Company',
        vendorReference: 'VREF-001',
        email: 'vendor@example.com',
        financeEmail: 'finance@example.com',
        mobile: '+974 1234 5678',
        status: 'Draft',
        vendorType: 'Organization',
        localOrForeign: 'Local',
        country: 'Qatar',
        currency: 'QAR',
        registeredDate: '2020-01-15T00:00:00',
        registrationDate: '2020-01-15T00:00:00',
        addressDescriptionPrimary: 'Main Office',
        streetPrimary: 'Al Corniche Street, Building 123',
        streetSecondary: 'Secondary Branch Location',
        cityPrimary: 'Doha',
        citySecondary: 'Al Rayyan',
        countryRegionIdPrimary: 'QA',
        countryRegionIdSecondary: 'QA',
        zipCodePrimary: '12345',
        zipCodeSecondary: '54321',
        phonePrimary: '+974 1234 5678',
        phoneSecondary: '+974 9876 5432',
        website: 'https://www.mockvendor.com',
        methodOfPayment: 'BT',
        termsOfPayment: '30 Days',
        workingCapital: '5000000',
        noOfYears: '10',
        noOfEmployees: '50',
        numberofBranches: 2,
        organizationalstructure: 'Limited Liability Company',
        category: [
          { categoryID: 'CAT001', CategoryID: 'CAT001', id: '1', createdAt: '2024-01-01', createdBy: 'admin', modifiedAt: '2024-01-01', modifiedBy: 'admin' },
          { categoryID: 'CAT002', CategoryID: 'CAT002', id: '2', createdAt: '2024-01-01', createdBy: 'admin', modifiedAt: '2024-01-01', modifiedBy: 'admin' }
        ],
        subCategory: [
          { subCategoryID: 'SUBCAT001', SubCategoryID: 'SUBCAT001', id: '1', createdAt: '2024-01-01', createdBy: 'admin', modifiedAt: '2024-01-01', modifiedBy: 'admin' },
          { subCategoryID: 'SUBCAT002', SubCategoryID: 'SUBCAT002', id: '2', createdAt: '2024-01-01', createdBy: 'admin', modifiedAt: '2024-01-01', modifiedBy: 'admin' }
        ],
        contactPerson: [
          {
            FirstName: 'John',
            LastName: 'Doe',
            Title: 'CEO',
            Email: 'john.doe@example.com',
            LandlineNumber: '+974 1234 5678',
            MobileNumber: '+974 5555 1234'
          }
        ],
        identificationDetails: [],
        associatedCompanies: [],
        managementTeam: [],
        attachments: [],
        bankAccount: {
          name: 'Qatar National Bank',
          accountNumber: '1234567890',
          bankIBAN: 'QA12345678901234567890',
          swift: 'QNBAQAQA',
          branch: 'Doha Branch',
          beneficiaryName: 'Mock Vendor Company',
          email: 'bank@example.com',
          address: 'Bank Address, Doha',
          currencyCode: 'QAR',
          activeDate: '2024-01-15',
          bankCountry: 'Qatar',
          documents: []
        }
      }
    };
  }

  // ==================== Bank Accounts ====================
  private getBankAccountsResponse(): any {
    const base64Sample = 'data:application/pdf;base64,JVBERi0xLjQKJeLjz9MKMyAwIG9iago8PAovVHlwZSAvQ2F0YWxvZwovUGFnZXMgMSAwIFIKPj4KZW5kb2JqCjQgMCBvYmoKPDwKL1R5cGUgL091dGxpbmVzCi9PdXRsaW5lcyAxIDAgUgo+PgplbmRvYmoKMSAwIG9iago8PAovVHlwZSAvUGFnZXMKL0tpZHMgWzIgMCBSXQovQ291bnQgMQo+PgplbmRvYmoKMiAwIG9iago8PAovVHlwZSAvUGFnZQovUGFyZW50IDEgMCBSCi9SZXNvdXJjZXMgPDwKL0ZvbnQgPDwKL0YxIDMgMCBSIAo+Pgo+PgovTWVkaWFCb3ggWzAgMCA2MTIgNzkyXQovQ29udGVudHMgNSAwIFIKPj4KZW5kb2JqCjMgMCBvYmoKPDwKL1R5cGUgL0ZvbnQKL1N1YnR5cGUgL1R5cGUxCi9CYXNlRm9udCAvSGVsdmV0aWNhCj4+CmVuZG9iago1IDAgb2JqCjw8Ci9MZW5ndGggNDQKPj4Kc3RyZWFtCkJUCi9GMSAxMiBUZgooVGhpcyBpcyBhIHNhbXBsZSBQRkYgZmlsZSkKZW5kc3RyZWFtCmVuZG9iagp4cmVmCjAgNgowMDAwMDAwMDAwIDY1NTM1IGYgCjAwMDAwMDAwMDkgMDAwMDAgbiAKMDAwMDAwMDA1NCAwMDAwMCBuIAowMDAwMDAwMTEwIDAwMDAwIG4gCjAwMDAwMDAxNjIgMDAwMDAgbiAKMDAwMDAwMDI0OCAwMDAwMCBuIAp0cmFpbGVyCjw8Ci9TaXplIDYKL1Jvb3QgNCAwIFIKPj4Kc3RhcnR4cmVmCjMxMgolJUVPRgo=';

    return {
      isSuccess: true,
      isSystemError: false,
      code: 200,
      data: [
        {
          id: 1,
          bankID: '1',
          name: '1', // Qatar National Bank ID from lookup
          forignBankName: '',
          bankType: 1,
          bankIBAN: 'QA12345678901234567890',
          swift: 'QNBAQAQA', // Component uses 'swift'
          swiftCode: 'QNBAQAQA', // Model property
          branch: 'Doha Main Branch', // Component uses 'branch'
          branchName: 'Doha Main Branch', // Model property
          beneficiaryName: 'Mock Vendor Company', // Component uses 'beneficiaryName'
          benficeryName: 'Mock Vendor Company', // Model property
          accountNumber: '1234567890',
          email: 'bank@qnb.com.qa',
          address: 'Al Corniche Street, Building 123, Doha, Qatar',
          currencyCode: 'QAR', // Component uses 'currencyCode'
          currency: 'QAR', // Model property
          bankCountry: 'QA',
          bankStatus: 1, // Active
          activeDate: '2024-01-15T00:00:00',
          activeFrom: new Date('2024-01-15T00:00:00'),
          ActiveDate: new Date('2024-01-15T00:00:00'),
          expiryDate: '2025-01-15T00:00:00',
          ExpiryDate: new Date('2025-01-15T00:00:00'),
          countryID: 1,
          vendorID: 1,
          addedOn: new Date('2024-01-15T00:00:00'),
          lastModification: new Date('2024-01-15T00:00:00'),
          bankDocument: '',
          sortCode: '',
          otherBankName: null,
          document: [ // Component uses 'document'
            {
              attachmentId: 'DOC001',
              fileName: 'Bank Account Statement.pdf',
              fileContent: base64Sample,
              filePath: '/documents/bank/BANK001/statement.pdf',
              mimeType: 'application/pdf',
              expiryDate: new Date('2025-01-15T00:00:00')
            },
            {
              attachmentId: 'DOC002',
              fileName: 'Bank Authorization Letter.pdf',
              fileContent: base64Sample,
              filePath: '/documents/bank/BANK001/authorization.pdf',
              mimeType: 'application/pdf',
              expiryDate: new Date('2025-01-15T00:00:00')
            }
          ],
          TheDocuments: [ // Model property
            {
              attachmentId: 'DOC001',
              fileName: 'Bank Account Statement.pdf',
              fileContent: base64Sample,
              filePath: '/documents/bank/BANK001/statement.pdf',
              expiryDate: new Date('2025-01-15T00:00:00')
            },
            {
              attachmentId: 'DOC002',
              fileName: 'Bank Authorization Letter.pdf',
              fileContent: base64Sample,
              filePath: '/documents/bank/BANK001/authorization.pdf',
              expiryDate: new Date('2025-01-15T00:00:00')
            }
          ]
        },
        {
          id: 2,
          bankID: '2',
          name: '2', // Commercial Bank ID from lookup
          forignBankName: '',
          bankType: 1,
          bankIBAN: 'QA98765432109876543210',
          swift: 'CBQAQAQA',
          swiftCode: 'CBQAQAQA',
          branch: 'West Bay Branch',
          branchName: 'West Bay Branch',
          beneficiaryName: 'Mock Vendor Company',
          benficeryName: 'Mock Vendor Company',
          accountNumber: '9876543210',
          email: 'bank@cbbank.com.qa',
          address: 'West Bay Area, Tower 456, Doha, Qatar',
          currencyCode: 'QAR',
          currency: 'QAR',
          bankCountry: 'QA',
          bankStatus: 1, // Active
          activeDate: '2024-02-01T00:00:00',
          activeFrom: new Date('2024-02-01T00:00:00'),
          ActiveDate: new Date('2024-02-01T00:00:00'),
          expiryDate: '2025-02-01T00:00:00',
          ExpiryDate: new Date('2025-02-01T00:00:00'),
          countryID: 1,
          vendorID: 1,
          addedOn: new Date('2024-02-01T00:00:00'),
          lastModification: new Date('2024-02-01T00:00:00'),
          bankDocument: '',
          sortCode: '',
          otherBankName: null,
          document: [
            {
              attachmentId: 'DOC003',
              fileName: 'Commercial Bank Account Details.pdf',
              fileContent: base64Sample,
              filePath: '/documents/bank/BANK002/details.pdf',
              mimeType: 'application/pdf',
              expiryDate: new Date('2025-02-01T00:00:00')
            }
          ],
          TheDocuments: [
            {
              attachmentId: 'DOC003',
              fileName: 'Commercial Bank Account Details.pdf',
              fileContent: base64Sample,
              filePath: '/documents/bank/BANK002/details.pdf',
              expiryDate: new Date('2025-02-01T00:00:00')
            }
          ]
        },
        {
          id: 3,
          bankID: 'other',
          name: 'other', // Other bank option
          forignBankName: '',
          bankType: 2,
          bankIBAN: 'AE123456789012345678901234',
          swift: 'ADCBUAEAA',
          swiftCode: 'ADCBUAEAA',
          branch: 'Dubai Main Branch',
          branchName: 'Dubai Main Branch',
          beneficiaryName: 'Mock Vendor Company',
          benficeryName: 'Mock Vendor Company',
          accountNumber: '5555555555',
          email: 'bank@adcb.com',
          address: 'Sheikh Zayed Road, Dubai, UAE',
          currencyCode: 'AED',
          currency: 'AED',
          bankCountry: 'AE',
          bankStatus: 0, // Pending/Inactive
          activeDate: '2024-03-01T00:00:00',
          activeFrom: new Date('2024-03-01T00:00:00'),
          ActiveDate: new Date('2024-03-01T00:00:00'),
          expiryDate: null,
          ExpiryDate: null,
          countryID: 2,
          vendorID: 1,
          addedOn: new Date('2024-03-01T00:00:00'),
          lastModification: new Date('2024-03-01T00:00:00'),
          bankDocument: '',
          sortCode: '',
          otherBankName: 'Commercial Bank', // Required when name is 'other'
          document: [
            {
              attachmentId: 'DOC004',
              fileName: 'ADCB Account Certificate.pdf',
              fileContent: base64Sample,
              filePath: '/documents/bank/BANK003/certificate.pdf',
              mimeType: 'application/pdf',
              expiryDate: null
            }
          ],
          TheDocuments: [
            {
              attachmentId: 'DOC004',
              fileName: 'ADCB Account Certificate.pdf',
              fileContent: base64Sample,
              filePath: '/documents/bank/BANK003/certificate.pdf',
              expiryDate: null
            }
          ]
        }
      ],
      totalCount: 3
    };
  }

  // ==================== Lookups ====================
  private getLookupsResponse(): any {
    return {
      isSuccess: true,
      countries: [
        { countryId: 'QA', countryName: 'Qatar', countryRegionId: 'QA' },
        { countryId: 'SA', countryName: 'Saudi Arabia', countryRegionId: 'SA' },
        { countryId: 'AE', countryName: 'United Arab Emirates', countryRegionId: 'AE' }
      ],
      currencies: [
        { currencyId: 'QAR', description: 'Qatari Riyal' },
        { currencyId: 'USD', description: 'US Dollar' },
        { currencyId: 'EUR', description: 'Euro' }
      ],
      methodsOfPayment: [
        { id: '1', name: 'Bank Transfer' },
        { id: '2', name: 'Cheque' },
        { id: '3', name: 'Credit Card' }
      ],
      categories: [
        { categoryId: 'CAT001', categoryName: 'Construction' },
        { categoryId: 'CAT002', categoryName: 'IT Services' },
        { categoryId: 'CAT003', categoryName: 'Maintenance' }
      ],
      subCategories: [
        { subCategoryId: 'SUBCAT001', subCategoryName: 'Building Construction', categoryId: 'CAT001' },
        { subCategoryId: 'SUBCAT002', subCategoryName: 'Software Development', categoryId: 'CAT002' }
      ],
      termsOfPayment: [
        { id: '1', name: 'Net 30' },
        { id: '2', name: 'Net 60' },
        { id: '3', name: 'Immediate' }
      ],
      preQualifications: [
        { id: '1', name: 'ISO Certified' },
        { id: '2', name: 'Bidaya Registered' }
      ],
      banks: [
        { bankId: '1', bankName: 'Qatar National Bank' },
        { bankId: '2', bankName: 'Commercial Bank' },
        { bankId: '3', bankName: 'Doha Bank' }
      ],
      documentsMaster: [
        { documentId: 'DOC001', documentName: 'Commercial Registration', parmDocumentFor: 'Vendor', processName: 'Registration' },
        { documentId: 'DOC002', documentName: 'Tax Certificate', parmDocumentFor: 'Vendor', processName: 'Registration' }
      ]
    };
  }

  // ==================== Dashboard ====================
  private getDashboardDataResponse(): any {
    return {
      isSuccess: true,
      data: {
        vendor: {
          vendorID: 'VENDOR001',
          vendorName: 'Mock Vendor Company',
          status: 'Approved',
          applicationSubmissionDate: '2024-01-15T00:00:00',
          lastApplicationModifyDate: '2024-02-01T00:00:00'
        },
        tender: {
          publicCount: 2,
          privateCount: 1,
          latestFiveTenders: [
            {
              tenderID: 'TND001',
              referenceNo: 'REF-2024-001',
              description: 'Construction of Office Building in Doha',
              startDate: '2024-01-15T00:00:00',
              closureDate: '2024-02-15T00:00:00',
              status: 'Open',
              typeofTender: 'Public'
            },
            {
              tenderID: 'TND002',
              referenceNo: 'REF-2024-002',
              description: 'IT Infrastructure Upgrade Project',
              startDate: '2024-02-01T00:00:00',
              closureDate: '2024-03-01T00:00:00',
              status: 'Closed',
              typeofTender: 'Public'
            },
            {
              tenderID: 'TND003',
              referenceNo: 'REF-2024-003',
              description: 'Annual Maintenance Services',
              startDate: '2024-01-20T00:00:00',
              closureDate: '2024-02-20T00:00:00',
              status: 'Open',
              typeofTender: 'Private'
            },
            {
              tenderID: 'TND004',
              referenceNo: 'REF-2024-003',
              description: 'Annual Maintenance Services',
              startDate: '2024-01-20T00:00:00',
              closureDate: '2024-02-20T00:00:00',
              status: 'Closed',
              typeofTender: 'Private'
            },
            {
              tenderID: 'TND005',
              referenceNo: 'REF-2024-003',
              description: 'Annual Maintenance Services',
              startDate: '2024-01-20T00:00:00',
              closureDate: '2024-02-20T00:00:00',
              status: 'Cancelled',
              typeofTender: 'Private'
            }
          ]
        },
        bid: {
          open: 2,
          submitted: 1
        },
        totalTenders: 15,
        activeTenders: 8,
        submittedBids: 12,
        awardedBids: 3,
        pendingPurchaseOrders: 5,
        totalInvoices: 25,
        pendingInvoices: 3,
        totalPayments: 18,
        pendingPayments: 2,
        unreadMessages: 5,
        unreadNotifications: 8
      }
    };
  }

  // ==================== Documents ====================
  private getDocumentsResponse(): any {
    return {
      isSuccess: true,
      result: [
        {
          documentId: 'DOC001',
          documentName: 'Commercial Registration',
          documentType: 'CR',
          uploadedDate: '2024-01-15T00:00:00',
          status: 'Approved',
          fileSize: 1024000,
          fileName: 'commercial_registration.pdf'
        },
        {
          documentId: 'DOC002',
          documentName: 'Tax Certificate',
          documentType: 'Tax',
          uploadedDate: '2024-01-20T00:00:00',
          status: 'Pending',
          fileSize: 512000,
          fileName: 'tax_certificate.pdf'
        }
      ],
      totalCount: 2
    };
  }

  // ==================== Submit Operations ====================
  private getSubmitTenderResponse(body?: any): any {
    return {
      isSuccess: true,
      data: {
        tenderID: 'TND' + Math.floor(Math.random() * 1000),
        referenceNo: 'REF-' + new Date().getFullYear() + '-' + Math.floor(Math.random() * 1000),
        submittedAt: new Date().toISOString(),
        status: 'Submitted'
      },
      message: 'Tender submitted successfully'
    };
  }

  private getSubmitBidResponse(body?: any): any {
    return {
      isSuccess: true,
      data: {
        bidID: 'BID' + Math.floor(Math.random() * 1000),
        tenderID: body?.tenderID || 'TND001',
        status: 'Submitted',
        submittedAt: new Date().toISOString(),
        bidValue: body?.bidValue || null
      },
      message: 'Bid submitted successfully'
    };
  }

  private getConfirmPOResponse(): any {
    return {
      isSuccess: true,
      data: {
        confirmed: true,
        confirmedAt: new Date().toISOString()
      },
      message: 'Purchase Order confirmed successfully'
    };
  }

  private getCreateInvoiceResponse(body?: any): any {
    return {
      isSuccess: true,
      data: {
        invoiceId: 'INV' + Math.floor(Math.random() * 1000),
        invoiceNumber: body?.invoiceNumber || 'INV-' + new Date().getFullYear() + '-' + Math.floor(Math.random() * 1000),
        status: 'Draft',
        createdAt: new Date().toISOString()
      },
      message: 'Invoice created successfully'
    };
  }

  private getRequestGRNResponse(body?: any): any {
    return {
      isSuccess: true,
      data: {
        grnId: 'GRN' + Math.floor(Math.random() * 1000),
        grnNumber: 'GRN-' + new Date().getFullYear() + '-' + Math.floor(Math.random() * 1000),
        status: 'Draft',
        requestedAt: new Date().toISOString()
      },
      message: 'GRN requested successfully'
    };
  }

  private getSendMessageResponse(body?: any): any {
    return {
      isSuccess: true,
      data: {
        messageId: 'MSG' + Math.floor(Math.random() * 1000),
        sentAt: new Date().toISOString()
      },
      message: 'Message sent successfully'
    };
  }

  private getRegisterVendorResponse(body?: any): any {
    return {
      isSuccess: true,
      data: {
        vendorID: 'VENDOR' + Math.floor(Math.random() * 1000),
        vendorReference: 'VREF-' + Math.floor(Math.random() * 1000),
        registeredAt: new Date().toISOString(),
        status: 'Pending Approval'
      },
      message: 'Vendor registration submitted successfully'
    };
  }

  private getUpdateVendorResponse(body?: any): any {
    return {
      isSuccess: true,
      data: {
        vendorID: body?.vendorID || 'VENDOR001',
        updatedAt: new Date().toISOString()
      },
      message: 'Vendor profile updated successfully'
    };
  }

  private getSubmitBankAccountResponse(body?: any): any {
    return {
      isSuccess: true,
      data: {
        bankAccountId: 'BANK' + Math.floor(Math.random() * 1000),
        submittedAt: new Date().toISOString(),
        status: 'Pending Approval'
      },
      message: 'Bank account submitted successfully'
    };
  }

  private getUpdateBankAccountResponse(body?: any): any {
    return {
      isSuccess: true,
      data: {
        bankAccountId: body?.bankAccountId || 'BANK001',
        updatedAt: new Date().toISOString()
      },
      message: 'Bank account updated successfully'
    };
  }

  private getUploadDocumentsResponse(body?: any): any {
    return {
      isSuccess: true,
      data: {
        documentIds: [
          'DOC' + Math.floor(Math.random() * 1000),
          'DOC' + Math.floor(Math.random() * 1000)
        ],
        uploadedAt: new Date().toISOString()
      },
      message: 'Documents uploaded successfully'
    };
  }

  // ==================== Default Response ====================
  private getDefaultResponse(method: string): any {
    const baseResponse: any = {
      isSuccess: true,
      isSystemError: false,
      code: 200,
      data: null,
      message: 'Mock response - Backend disabled'
    };

    if (method === 'POST' || method === 'PUT' || method === 'PATCH') {
      baseResponse.data = {
        id: Math.floor(Math.random() * 1000),
        createdAt: new Date().toISOString()
      };
    }

    if (method === 'DELETE') {
      baseResponse.deleted = true;
    }

    return baseResponse;
  }
}

